"use client"

import styled from "@emotion/styled"
import { Box } from "@mui/material"
import { useInView } from "react-intersection-observer"
import { mediaQueries, useInViewConfig, useTransitionUpStyles } from "ui"
import LottiePlayer from "ui/common/bit/LottiePlayer"
import { isLottieFile } from "ui/common/bit/isLottieFile"
import { getUmbracoMediaUrl } from "utils"
import BlockWrapper from "../common/BlockWrapper"
import ApplicationImageAndContentBlock from "./ApplicationImageAndContentBlock/ApplicationImageAndContentBlock"
import { ApplicationImageAndContentBlockProps } from "./ApplicationImageAndContentBlock/types"

const StyledContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${mediaQueries.desktopM} {
    flex-direction: row;
  }
`

const StyledApplicationImageAndContentBlock = styled(
  ApplicationImageAndContentBlock,
)`
  ${mediaQueries.desktopM} {
    flex-direction: row-reverse;
  }
`

interface ApplicationImageAndContentWrapperProps {
  data: any
  id?: string
}

const ApplicationImageAndContentWrapper = ({
  data,
  id,
}: ApplicationImageAndContentWrapperProps) => {
  const { ref, inView } = useInView(useInViewConfig)
  const transitionStyles = useTransitionUpStyles(inView)
  const { backgroundColor } = data
  // Process all properties into a single object with the correct structure
  const blockProps: ApplicationImageAndContentBlockProps = {
    subtitle: data?.subtitle?.value || "",
    title: data?.title?.value || "",
    text: data?.text?.richText || "",
    appTitle: data?.appTitle?.value || "",
    appStoreLink: data?.appStoreLink?.links?.[0] && {
      text: data.appStoreLink.links[0].name || "",
      href: data.appStoreLink.links[0].url || "",
    },
    appStoreLogo: data?.appStoreLogo?.mediaItems?.[0] && {
      src: data.appStoreLogo.mediaItems[0].url
        ? getUmbracoMediaUrl(data.appStoreLogo.mediaItems[0].url)
        : "",
      alt: data.appStoreLogo.mediaItems[0].altText || "App Store",
      width: 135,
      height: 40,
    },
    googlePlayLink: data?.googlePlayLink?.links?.[0] && {
      text: data.googlePlayLink.links[0].name || "",
      href: data.googlePlayLink.links[0].url || "",
    },
    googlePlayLogo: data?.googlePlayLogo?.mediaItems?.[0] && {
      src: data.googlePlayLogo.mediaItems[0].url
        ? getUmbracoMediaUrl(data.googlePlayLogo.mediaItems[0].url)
        : "",
      alt: data.googlePlayLogo.mediaItems[0].altText || "Google Play",
      width: 135,
      height: 40,
    },
    image: data?.image?.mediaItems?.[0] && {
      src: data.image.mediaItems[0].url
        ? getUmbracoMediaUrl(data.image.mediaItems[0].url)
        : "",
      alt:
        data.image.mediaItems[0].altText ||
        data?.title?.value ||
        "Application image",
      width: 650, // Default width for desktop as seen in the component styles
      height: 550, // Default height for desktop as seen in the component styles
      isLottie: isLottieFile(getUmbracoMediaUrl(data.image.mediaItems[0].url)),
    },
  }

  return (
    <BlockWrapper id={id} bgColor={backgroundColor?.value}>
      <StyledContainer ref={ref} style={transitionStyles}>
        {/* Render Lottie if image is Lottie, else pass as usual */}
        {blockProps.image?.isLottie ? (
          <LottiePlayer
            lottieUrl={blockProps.image.src}
            width={blockProps.image.width}
            height={blockProps.image.height}
            playOnce={true}
            autoplay={true}
            loop={false}
            ariaLabel={blockProps.image.alt}
          />
        ) : (
          <StyledApplicationImageAndContentBlock {...blockProps} />
        )}
      </StyledContainer>
    </BlockWrapper>
  )
}

export default ApplicationImageAndContentWrapper
